import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, FileTextIcon, XIcon } from 'lucide-react';
import { FactIcon } from './FactIcon';
import { getTreatmentFacts } from '../../utils/treatmentFacts';
import type { Treatment } from '../../types/care';
interface TreatmentDrawerProps {
  treatment: Treatment | null;
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function TreatmentDrawer({
  treatment,
  onClose
}: TreatmentDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!treatment) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [treatment, onClose]);
  return <AnimatePresence data-id="element-301" data-mp="2pqhu2ah3ismf">
      {treatment && <div className="fixed inset-0 z-50" role="presentation" data-id="element-302" data-mp="2pqq6y2d7xxxm">
          <motion.div className="absolute inset-0 bg-ink/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} data-id="element-303" data-mp="2upugwv7yvzp0" />
          <motion.aside role="dialog" aria-modal="true" aria-labelledby="drawer-titel" className="absolute inset-y-0 right-0 flex w-full max-w-[560px] flex-col bg-white shadow-2xl" initial={{
        x: 40,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 40,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: EASE
      }} data-id="element-304" data-mp="222gn35zkfk94">
            <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-5 sm:px-8" data-id="element-305" data-mp="1nb7h4yorm01w">
              <div data-id="element-306" data-mp="1idfeb9ml0tix">
                <p className="text-sm font-medium text-primary" data-id="element-307" data-mp="1ibun9tzh7spt">{treatment.category}</p>
                <h2 id="drawer-titel" className="mt-1 font-display text-2xl font-semibold leading-tight text-ink" data-id="element-308" data-mp="1wbdsadwzqnxw">
                  {treatment.name}
                </h2>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink transition-colors duration-150 hover:bg-surface" aria-label="Sluiten" data-id="element-309" data-mp="14kfxn4b2ct9s">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-310" data-mp="18l9oo6jsyo6w" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-8" data-id="element-311" data-mp="kvp1fbgm94rt">
              <p className="text-lg leading-relaxed text-ink" data-id="element-312" data-mp="gulph01ovsvv">{treatment.summary}</p>

              {getTreatmentFacts(treatment.facts).length > 0 && <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line" data-id="element-313" data-mp="6pvi2eninwk9">
                  {getTreatmentFacts(treatment.facts).map(fact => <div key={fact.kind} className="bg-white p-4" data-id="element-314" data-mp="3swtubki3hg6d">
                      <dt className="flex items-center gap-2 text-xs font-medium text-muted" data-id="element-315" data-mp="1tew9r3shuac">
                        <FactIcon kind={fact.kind} className="h-3.5 w-3.5 text-primary" data-id="element-316" data-mp="pjizhxd2fs0t" />
                        {fact.label}
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-ink" data-id="element-317" data-mp="fk1ttfxtde4u">{fact.value}</dd>
                    </div>)}
                </dl>}

              <section className="mt-8" data-id="element-318" data-mp="kha4ywkjohwl">
                <h3 className="font-display text-lg font-semibold text-ink" data-id="element-319" data-mp="309r89ruluxx0">Wat is het?</h3>
                <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-muted" data-id="element-320" data-mp="3593v4clh84zl">
                  {treatment.intro.map(p => <p key={p} data-id="element-321" data-mp="3lha1mmudw2al">{p}</p>)}
                </div>
              </section>

              <section className="mt-8" data-id="element-322" data-mp="3gp04alqxvzya">
                <h3 className="font-display text-lg font-semibold text-ink" data-id="element-323" data-mp="3bqosi5h33fdf">Hoe verloopt de behandeling?</h3>
                <ol className="mt-3 space-y-4" data-id="element-324" data-mp="36ruc1a6la2wh">
                  {treatment.procedure.map((p, i) => <li key={p} className="flex gap-4" data-id="element-325" data-mp="89oq9l9v5yxl" data-mpi={i}>
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary" data-id="element-326" data-mp="896wqwel5ytd">
                        {i + 1}
                      </span>
                      <p className="text-[15px] leading-relaxed text-muted" data-id="element-327" data-mp="3uizqbkwvsn4o">{p}</p>
                    </li>)}
                </ol>
              </section>

              {treatment.report && <section className="mt-8" data-id="element-328" data-mp="2e2wtd5ygu437">
                  <h3 className="font-display text-lg font-semibold text-ink" data-id="element-329" data-mp="2e1vl198u3vp6">Geef vooraf door</h3>
                  <ul className="mt-3 flex flex-wrap gap-2" data-id="element-330" data-mp="tlxhvo9je5xw">
                    {treatment.report.map(item => <li key={item} className="rounded-full bg-surface px-3 py-1.5 text-sm text-ink ring-1 ring-line" data-id="element-331" data-mp="ykidfadug1g0">
                        {item}
                      </li>)}
                  </ul>
                </section>}
            </div>

            <footer className="grid gap-3 border-t border-line px-6 py-5 sm:grid-cols-2 sm:px-8" data-id="element-332" data-mp="1d7ttnpkz09mg">
              <Link to={`/behandelingen/${treatment.slug}`} onClick={onClose} className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-5 py-3 text-[15px] font-semibold text-white transition-colors duration-150 hover:bg-primary-dark" data-id="element-333" data-mp="1h9f83fd5kvyr">
                Volledige informatie
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-334" data-mp="3szhl55h7t9xv" />
              </Link>
              {treatment.pdf && <a href={treatment.pdf} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-line px-5 py-3 text-[15px] font-semibold text-ink transition-colors duration-150 hover:border-primary hover:text-primary" data-id="element-335" data-mp="3l035wz22vvuo">
                  <FileTextIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-336" data-mp="7s02ld1xi0fk" />
                  Patiëntenfolder
                </a>}
            </footer>
          </motion.aside>
        </div>}
    </AnimatePresence>;
}